import { defineComponent, ref } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, container, loader, loadingButton, pageTitle } from '@/ui/components';
import { useTask } from '@/ui/concerns';
import { create } from '@/services/api/resources/role';
import { filter as fetchAllUsers } from '@/services/api/resources/user';
import { filterListToOptions, toastAndRedirect } from '@/ui/helpers/uiHelper';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { SelectOptions } from '@/ui/types';
import { useRoleForm } from './useRoleForm';
import { useRoleRecord } from './useRoleRecord';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import { useCreatableUserModal } from '@/ui/pages/useCreatableUserModal';
import { AdministrationTabNames, queryTabName } from '@/ui/components/navigation/constants';

export const RoleCreatePage = defineComponent({
  name: 'RoleCreatePage',
  setup() {
    if (!hasFeature('roles')) {
      return renderResourceTeaserPage(t('common.roles'), t('role.teaser'), t('common.teaser'), 'roleIndex');
    }

    if (!hasPermission(Permissions.MANAGE_PERMISSIONS)) {
      return renderForbiddenPage;
    }

    const userOptions = ref<SelectOptions>([]);

    // create empty record
    const record = useRoleRecord();

    // get record
    const getUsers = useTask(
      () => fetchAllUsers(),
      users => {
        userOptions.value = filterListToOptions(users);
      },
    );

    // send record
    const sendRecord = useTask(
      () => create(record.value),
      () => {
        toastAndRedirect(t('role.created'), 'administration', { [queryTabName]: AdministrationTabNames.ROLE });
      },
    );

    // form
    const { formFields, handleSubmit, isLoading } = useRoleForm(record, sendRecord);

    useCreatableUserModal(userOptions, id => {
      record.value.users.push(id);
    });

    return function render() {
      return container(
        [
          pageTitle(t('role.create')),
          box([
            loader('form', getUsers, [
              ...formFields(userOptions.value),
              loadingButton(
                t('role.create'),
                handleSubmit,
                isLoading.value,
              ),
            ]),
          ]),
        ],
        'narrow',
      );
    };
  },
});
